import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  CircularProgress,
  Button,
  Autocomplete,
  useTheme,
  useMediaQuery
} from '@mui/material';
import axios from '../../services/axiosConfig';
import { environment } from '../../config/environment';
import { useAuth } from '../../context/AuthContext';

interface IRedemption {
  couponCode: string;
  user: string;
  customerNo: string;
  invoiceRef: string;
  productCode: string;
  dateRedeemed: string;
  quantity: number;
  totalAmount: number;
}

interface IRedemptionCouponCode {
  couponId: string;
  couponCode: string;
}

interface IRedemptionFilterData {
  users: string[];
  invoiceReferences: string[];
  productCodes: string[];
  couponCodes: IRedemptionCouponCode[];
  accountNumbers: string[];
}

const Reports: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const [redemptions, setRedemptions] = useState<IRedemption[]>([]);
  const [loading, setLoading] = useState(false);
  const [filterData, setFilterData] = useState<IRedemptionFilterData | null>(null);
  const [couponId, setCouponId] = useState('');
  const [username, setUsername] = useState('');
  const [accountNo, setAccountNo] = useState('');
  const [invoiceReference, setInvoiceReference] = useState('');
  const [productCode, setProductCode] = useState('');
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [startTime, setStartTime] = useState('00:00');
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [endTime, setEndTime] = useState('23:59');
  const [totalQuantity, setTotalQuantity] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const { hasRole, user } = useAuth();

  const combineDateAndTime = (date: string, time: string): string => {
    return `${date}T${time}:00`;
  };

  const loadFilterData = useCallback(async () => {
    try {
      const response = await axios.get(`${environment.apiUrl}report/redemption/filterdata`);
      setFilterData(response.data);
    } catch (error) {
      console.error('Error loading filter data:', error);
    }
  }, []);

  const loadRedemptions = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (couponId) params.couponId = couponId;
      if (username) params.username = username;
      if (accountNo) params.accountNo = accountNo;
      if (invoiceReference) params.invoiceReference = invoiceReference;
      if (productCode) params.productCode = productCode;
      if (startDate && startTime) params.startDate = combineDateAndTime(startDate, startTime);
      if (endDate && endTime) params.endDate = combineDateAndTime(endDate, endTime);

      const response = await axios.get(`${environment.apiUrl}report/redemption`, { params });
      console.log('Redemptions response:', response.data);

      const redemptionsData = Array.isArray(response.data) ? response.data : [];
      setRedemptions(redemptionsData);

      // Calculate totals
      let qty = 0;
      let amt = 0;
      for (const row of redemptionsData) {
        qty += row.quantity;
        amt += row.totalAmount;
      }
      setTotalQuantity(qty);
      setTotalAmount(amt);
    } catch (error) {
      console.error('Error loading redemptions:', error);
      setRedemptions([]);
      setTotalQuantity(0);
      setTotalAmount(0);
    } finally {
      setLoading(false);
    }
  }, [couponId, username, accountNo, invoiceReference, productCode, startDate, startTime, endDate, endTime]);

  useEffect(() => {
    loadFilterData();
  }, [loadFilterData]);

  useEffect(() => {
    // If user has 'redeemer' role, auto-fill username
    if (hasRole(['redeemer']) && user && filterData) {
      setUsername(user.username);
    }
    if (filterData) {
      loadRedemptions();
    }
  }, [hasRole, user, filterData, loadRedemptions]);

  const handleSearch = () => {
    loadRedemptions();
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount);
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, width: '100%' }}>
      <Typography variant="h5" sx={{ fontWeight: 500, color: 'text.secondary' }} gutterBottom>
        Redemption Reports
      </Typography>

      <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <Autocomplete
              sx={{ flex: '1 1 200px', minWidth: '150px' }}
              size="small"
              options={filterData?.couponCodes || []}
              getOptionLabel={(option) => typeof option === 'string' ? option : option.couponCode || ''}
              value={filterData?.couponCodes?.find(c => c.couponId === couponId) || null}
              onChange={(event, newValue) => {
                setCouponId(newValue ? (newValue as IRedemptionCouponCode).couponId : '');
              }}
              renderInput={(params) => (
                <TextField {...params} label="Coupon Code" />
              )}
              isOptionEqualToValue={(option, value) => 
                (option as IRedemptionCouponCode).couponId === (value as IRedemptionCouponCode).couponId
              }
            />
            <Autocomplete
              sx={{ flex: '1 1 200px', minWidth: '150px' }}
              size="small"
              options={filterData?.users || []}
              value={username || null}
              onChange={(event, newValue) => {
                setUsername(newValue || '');
              }}
              renderInput={(params) => (
                <TextField {...params} label="User" />
              )}
            />
            <Autocomplete
              sx={{ flex: '1 1 200px', minWidth: '150px' }}
              size="small"
              options={filterData?.accountNumbers || []}
              value={accountNo || null}
              onChange={(event, newValue) => {
                setAccountNo(newValue || '');
              }}
              renderInput={(params) => (
                <TextField {...params} label="Customer No" />
              )}
            />
            <Autocomplete
              sx={{ flex: '1 1 200px', minWidth: '150px' }}
              size="small"
              options={filterData?.invoiceReferences || []}
              value={invoiceReference || null}
              onChange={(event, newValue) => {
                setInvoiceReference(newValue || '');
              }}
              renderInput={(params) => (
                <TextField {...params} label="Invoice Ref" />
              )}
            />
            <Autocomplete
              sx={{ flex: '1 1 200px', minWidth: '150px' }}
              size="small"
              options={filterData?.productCodes || []}
              value={productCode || null}
              onChange={(event, newValue) => {
                setProductCode(newValue || '');
              }}
              renderInput={(params) => (
                <TextField {...params} label="Product Code" />
              )}
            />
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <TextField
              sx={{ flex: '1 1 200px', minWidth: '150px' }}
              label="Start Date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              sx={{ flex: '1 1 200px', minWidth: '150px' }}
              label="Start Time"
              type="time"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              sx={{ flex: '1 1 200px', minWidth: '150px' }}
              label="End Date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              sx={{ flex: '1 1 200px', minWidth: '150px' }}
              label="End Time"
              type="time"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </Box>
          <Button 
            variant="contained" 
            onClick={handleSearch}
            sx={{ minWidth: '120px', height: '40px' }}
          >
            Search
          </Button>
        </Box>
      </Paper>

      {loading ? (
        <Box display="flex" justifyContent="center" p={6}>
          <CircularProgress size={48} />
        </Box>
      ) : (
        <>
          <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-around', flexWrap: 'wrap', gap: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                <strong>Total Quantity:</strong> {totalQuantity}
              </Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                <strong>Total Amount:</strong> {formatCurrency(totalAmount)}
              </Typography>
            </Box>
          </Paper>

          <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table sx={{ minWidth: 650, tableLayout: 'auto' }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, minWidth: 120 }}>Coupon Code</TableCell>
                    <TableCell sx={{ fontWeight: 600, display: { xs: 'none', md: 'table-cell' }, minWidth: 100 }}>User</TableCell>
                    <TableCell sx={{ fontWeight: 600, minWidth: 100 }}>Customer No</TableCell>
                    <TableCell sx={{ fontWeight: 600, display: { xs: 'none', lg: 'table-cell' }, minWidth: 120 }}>Invoice Ref</TableCell>
                    <TableCell sx={{ fontWeight: 600, display: { xs: 'none', md: 'table-cell' }, minWidth: 100 }}>Product Code</TableCell>
                    <TableCell sx={{ fontWeight: 600, display: { xs: 'none', lg: 'table-cell' }, minWidth: 150 }}>Date Redeemed</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, minWidth: 80 }}>Quantity</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, minWidth: 100 }}>Total Amount</TableCell>
                  </TableRow>
                </TableHead>
              <TableBody>
                {redemptions.map((redemption, index) => (
                  <TableRow 
                    key={index}
                    sx={{
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      },
                    }}
                  >
                    <TableCell sx={{ wordBreak: 'break-word' }}>{redemption.couponCode}</TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' }, wordBreak: 'break-word' }}>{redemption.user}</TableCell>
                    <TableCell sx={{ wordBreak: 'break-word' }}>{redemption.customerNo}</TableCell>
                    <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' }, wordBreak: 'break-word' }}>{redemption.invoiceRef}</TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' }, wordBreak: 'break-word' }}>{redemption.productCode}</TableCell>
                    <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                      {new Date(redemption.dateRedeemed).toLocaleString()}
                    </TableCell>
                    <TableCell align="right">{redemption.quantity}</TableCell>
                    <TableCell align="right">
                      {formatCurrency(redemption.totalAmount)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          </Paper>
        </>
      )}
    </Box>
  );
};

export default Reports;
